import type { CSSProperties } from "react";
import { useId } from "react";

import {
  EDGE_DRAW,
  EDGE_LOOP,
  MARK_HOLES,
  MARK_NODES,
  TEAMFORM_MARK_STYLES,
} from "./teamFormMark";

export type TeamFormMarkAnimation = "signal" | "assemble" | "ripple";

const MASK_HOLES = (
  <>
    <rect width="70" height="70" fill="white" />
    {MARK_HOLES.map((h) => (
      <circle
        key={`${h.cx}-${h.cy}`}
        cx={h.cx}
        cy={h.cy}
        r={h.r}
        fill="black"
      />
    ))}
  </>
);

const STYLES = TEAMFORM_MARK_STYLES as Record<TeamFormMarkAnimation, string>;

export type TeamFormMarkSpinnerProps = {
  size?: number;
  animation?: TeamFormMarkAnimation;
  accent?: string;
  ariaLabel?: string;
  decorative?: boolean;
  "data-testid"?: string;
};

export function TeamFormMarkSpinner({
  size = 48,
  animation = "signal",
  accent,
  ariaLabel = "Loading",
  decorative = false,
  "data-testid": dataTestId = "teamform-mark-spinner",
}: TeamFormMarkSpinnerProps) {
  const maskSuffix = useId().replace(/:/g, "");
  const classPrefix = `tfsp-${animation}-`;
  const css = STYLES[animation].replace(/\$/g, classPrefix);
  const holesMaskId = `${classPrefix}holes-${maskSuffix}`;
  const accentStyle: CSSProperties | undefined = accent
    ? ({ "--tfsp-accent": accent } as CSSProperties)
    : undefined;

  return (
    <svg
      className={`${classPrefix}root`}
      width={size}
      height={size}
      viewBox="0 0 70 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : ariaLabel}
      data-testid={dataTestId}
      style={accentStyle}
    >
      <style>{css}</style>
      <mask
        id={holesMaskId}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="70"
        height="70"
      >
        {MASK_HOLES}
      </mask>
      {animation === "signal" && (
        <g mask={`url(#${holesMaskId})`}>
          <path className={`${classPrefix}edge`} d={EDGE_LOOP} />
          <path
            className={`${classPrefix}comet`}
            pathLength={1000}
            d={EDGE_LOOP}
          />
        </g>
      )}
      {animation === "assemble" && (
        <g mask={`url(#${holesMaskId})`}>
          <path
            className={`${classPrefix}draw`}
            pathLength={1000}
            d={EDGE_DRAW}
          />
        </g>
      )}
      {animation === "ripple" && (
        <>
          <g mask={`url(#${holesMaskId})`}>
            <path className={`${classPrefix}edge`} d={EDGE_LOOP} />
          </g>
          <circle
            className={`${classPrefix}ring`}
            cx="19.97"
            cy="22.58"
            r="1"
          />
        </>
      )}
      {MARK_NODES.map((n) => (
        <circle
          key={n.name}
          className={`${classPrefix}node ${classPrefix}${n.name}`}
          cx={n.cx}
          cy={n.cy}
          r={n.r}
        />
      ))}
    </svg>
  );
}
